import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { Grid, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography } from "@mui/material";
import CommonTable from "components/commonTable/CommonTable";
import AllotDialog from "components/allotDialog/AllotDialog";
import CommonBreadCrumb from "components/commonBreadCrumb/CommonBreadCrumb";
import { useDispatch, useSelector } from "react-redux";
import {
  getAssignDriver,
  getTrip,
  assignSubvendorResponse,
} from "redux/actions/UserTrip";
import Loader from "components/loader/Loader";
import { updateAssignDriver, getRequestVehicle } from "redux/actions/UserAllot";

// Custom premium dialog for sub-vendor rejection reason
const RejectReasonDialog = ({ open, onClose, onSubmit }) => {
  const [reason, setReason] = useState("");
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="sm" 
      PaperProps={{ sx: { borderRadius: "12px", p: 2 } }}
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: "20px", color: "#1a237e" }}>
        Reject Trip Assignment
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Please specify the reason for rejecting this trip assignment.
        </Typography>
        <TextField
          autoFocus
          margin="dense"
          label="Rejection Reason"
          type="text"
          fullWidth
          variant="outlined"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
        />
      </DialogContent>
      <DialogActions sx={{ px: 3 }}>
        <Button onClick={onClose} sx={{ color: "grey.600", textTransform: "none" }}>Cancel</Button>
        <Button 
          onClick={() => onSubmit(reason)} 
          variant="contained" 
          color="error" 
          disabled={!reason.trim()}
          sx={{ borderRadius: "8px", px: 3, textTransform: "none" }}
        >
          Reject Trip
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function AssignTripView() {
  const [value, setValue] = useState("requested");
  const [openDialog, setOpenDialog] = useState(false);
  const dispatch = useDispatch();

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectTripId, setRejectTripId] = useState(null);

  const assignDriverData = useSelector(
    (state) => state?.userAssignDriver?.userAssignDriver?.data,
  );
  const assignDriverLoader = useSelector(
    (state) => state?.userAssignDriver?.loading,
  );
  const trips = useSelector((state) => state?.userTrip?.userTrip?.data);
  const loader = useSelector((state) => state?.userTrip?.loading);

  // ── Vehicle state ──
  const availableVehicles = useSelector(
    (state) => state?.userRequestVehicle?.userRequestVehicle?.data || [],
  );
  const availableVehiclesLoader = useSelector(
    (state) => state?.userRequestVehicle?.loading || false,
  );
  const [step, setStep] = useState("vehicle"); // 'vehicle' | 'driver'
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterDrivers, setFilterDrivers] = useState(assignDriverData);
  const [filterVehicles, setFilterVehicles] = useState(availableVehicles);
  const [tripId, setTripId] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [locationData, setLocationData] = useState({});

  const id = localStorage.getItem("companyId");
  const userId = localStorage.getItem("ID");

  let filterData = {
    assignId: id,
    status: value,
    page: page,
    pageSize: rowsPerPage,
    type: "",
  };
  let filterDataDriver = {
    assignId: userId,
    status: "assignTrip",
    page: "",
    pageSize: "",
    type: "",
  };

  useEffect(() => {
    filterAllDrivers();
  }, [searchQuery, assignDriverData]);

  useEffect(() => {
    setFilterDrivers(assignDriverData);
  }, [assignDriverData]);

  // ── Sync vehicle data ──
  useEffect(() => {
    filterAllVehicles();
  }, [searchQuery, availableVehicles]);

  useEffect(() => {
    setFilterVehicles(availableVehicles);
  }, [availableVehicles]);

  useEffect(() => {
    dispatch(getTrip(filterData));
  }, []);

  // ── Step 1 - Opens Vehicle Selection ──
  const onClickTableVendor = (data) => {
    if (value !== "cancelled") {
      setTripId(data?.id);
      setLocationData(data);
      setStep("vehicle");
      setSelectedVehicleId(null);
      setSearchQuery("");

      // Fetch available vehicles for this sub-vendor using correct action
      dispatch(getRequestVehicle({ type: "vehicleRegistration", page: 0, pageSize: 100 }));

      setOpenDialog(true);
    }
  };

  // ── Step 2 - Vehicle Confirmed, Switch to Driver ──
  const onVehicleConfirm = (vehicleData) => {
    setSelectedVehicleId(vehicleData?.id);
    setStep("driver");
    setSearchQuery("");

    // Now fetch drivers available for this vehicle/trip
    dispatch(getAssignDriver({ tripId: tripId }));
  };

  // ── Step 3 - Submit with vehicleId ──
  const submittAssign = (data) => {
    const val = {
      tripId: tripId,
      driverId: data?.id,
      vehicleId: selectedVehicleId,
      status: value,
      driverData: filterDataDriver,
    };
    dispatch(updateAssignDriver(val));
    handleClose();
  };

  // ── Reset both steps on close ──
  const handleClose = () => {
    setOpenDialog(false);
    setStep("vehicle");
    setSelectedVehicleId(null);
  };

  // ── Back to vehicle selection ──
  const handleBackToVehicles = () => {
    setStep("vehicle");
    setSearchQuery("");
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
    setPage(0);
    filterData = { ...filterData, status: newValue, page: 0 };
    dispatch(getTrip(filterData));
  };

  const breadcrumbPaths = [
    { name: "Assign Trip", url: "", className: "colorBlue" },
  ];

  const handelSearch = (val) => {
    setSearchQuery(val);
  };

  const filterAllDrivers = () => {
    let filteredDriverList = [];
    if (searchQuery !== "") {
      filteredDriverList = (assignDriverData || []).filter((driver) =>
        driver.driverName.toLowerCase().includes(searchQuery?.toLowerCase()),
      );
    } else {
      filteredDriverList = assignDriverData;
    }
    setFilterDrivers(filteredDriverList);
  };

  // ── Filter vehicles by search ──
  const filterAllVehicles = () => {
    let filteredVehicleList = [];
    if (searchQuery !== "") {
      filteredVehicleList = (availableVehicles || []).filter(
        (vehicle) =>
          vehicle?.regNumber
            ?.toLowerCase()
            ?.includes(searchQuery?.toLowerCase()) ||
          vehicle?.carModel
            ?.toLowerCase()
            ?.includes(searchQuery?.toLowerCase()),
      );
    } else {
      filteredVehicleList = availableVehicles;
    }
    setFilterVehicles(filteredVehicleList);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    filterData = { ...filterData, page: newPage };
    dispatch(getTrip(filterData));
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
    filterData = {
      ...filterData,
      page: 0,
      pageSize: parseInt(event.target.value),
    };
    dispatch(getTrip(filterData));
  };

  const onAcceptTrip = (trip) => {
    dispatch(
      assignSubvendorResponse({
        tripId: trip.id,
        action: "accept",
        callback: () => {
          dispatch(getTrip(filterData));
        },
      })
    );
  };

  const onRejectTrip = (trip) => {
    setRejectTripId(trip.id);
    setRejectDialogOpen(true);
  };

  const onConfirmReject = (reason) => {
    dispatch(
      assignSubvendorResponse({
        tripId: rejectTripId,
        action: "reject",
        rejectionReason: reason,
        callback: () => {
          setRejectDialogOpen(false);
          setRejectTripId(null);
          dispatch(getTrip(filterData));
        },
      })
    );
  };

  return (
    <div>
      <CommonBreadCrumb paths={breadcrumbPaths} />
      {loader ? (
        <Loader />
      ) : (
        <Grid container>
          <Grid item className="tableContainer" xs={12}>
            <Box className="contentBoxStyle">
              <TabContext value={value}>
                <Grid
                  container
                  className="tabBoxStyle"
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Grid item>
                    <TabList
                      className="minHeight32"
                      onChange={handleChange}
                      aria-label="lab API tabs example"
                      TabIndicatorProps={{
                        style: { backgroundColor: "white" },
                      }}
                    >
                      <Tab
                        label="Requested"
                        value="requested"
                        className="tabStyle"
                      />
                      <Tab
                        label="Assigned"
                        value="assigned"
                        className="tabStyle"
                      />
                      <Tab
                        label="Cancelled"
                        value="cancelled"
                        className="tabStyle"
                      />
                    </TabList>
                  </Grid>
                </Grid>
                <TabPanel
                  value="requested"
                  sx={{ width: "100%" }}
                  className="vendotTable"
                >
                  <CommonTable
                    data={trips}
                    onClickTableVendor={onClickTableVendor}
                    actionBtn="true"
                    tabName="assignTrip"
                    onAcceptTrip={onAcceptTrip}
                    onRejectTrip={onRejectTrip}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    isClickable={false}
                  />
                </TabPanel>
                <TabPanel value="cancelled" className="vendotTable">
                  <CommonTable
                    data={trips}
                    tabName="assignTrip"
                    onClickTableVendor={onClickTableVendor}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    isClickable={false}
                  />
                </TabPanel>
                <TabPanel value="assigned" className="vendotTable">
                  <CommonTable
                    data={trips}
                    onClickTableVendor={onClickTableVendor}
                    actionBtn="true"
                    tabName="assignTrip"
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    isClickable={false}
                  />
                </TabPanel>
              </TabContext>
            </Box>
          </Grid>
        </Grid>
      )}

      {/* ── STEP 1: Vehicle Selection Dialog ── */}
      {openDialog && step === "vehicle" && (
        <AllotDialog
          onSearch={handelSearch}
          type={"vechileView"}
          openDialog={openDialog}
          allotData={filterVehicles}
          title={"Available Vehicles"}
          subtitle={"Pickup location - " + locationData?.sourceAddress}
          titleType={"Vehicle"}
          placeholder={"Search vehicle (e.g. reg number, model)"}
          handleClose={handleClose}
          submittAssign={onVehicleConfirm}
          loader={availableVehiclesLoader}
          confirmButtonText="Select Vehicle"
        />
      )}

      {/* ── STEP 2: Driver Selection Dialog ── */}
      {openDialog && step === "driver" && (
        <AllotDialog
          onSearch={handelSearch}
          type={"tripView"}
          openDialog={openDialog}
          allotData={filterDrivers}
          title={"Available Drivers"}
          subtitle={"Pickup location - " + locationData?.sourceAddress}
          titleType={"Driver"}
          placeholder={"Search driver"}
          handleClose={handleBackToVehicles}
          submittAssign={submittAssign}
          loader={assignDriverLoader}
        />
      )}

      {/* ── Rejection Dialog ── */}
      <RejectReasonDialog
        open={rejectDialogOpen}
        onClose={() => {
          setRejectDialogOpen(false);
          setRejectTripId(null);
        }}
        onSubmit={onConfirmReject}
      />
    </div>
  );
}

export default AssignTripView;
